import { useState, useCallback } from 'react';

interface DialogState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  onConfirm?: () => void;
  showCancel?: boolean;
  confirmText?: string;
  cancelText?: string;
}

interface UseDialogReturn {
  dialogState: DialogState;
  showDialog: (config: Omit<DialogState, 'isOpen'>) => void;
  showSuccess: (title: string, message: string, onConfirm?: () => void) => void;
  showError: (title: string, message: string, onConfirm?: () => void) => void;
  showWarning: (title: string, message: string, onConfirm?: () => void) => void;
  showInfo: (title: string, message: string, onConfirm?: () => void) => void;
  showConfirm: (title: string, message: string, onConfirm: () => void, onCancel?: () => void) => void;
  closeDialog: () => void;
}

export const useDialog = (): UseDialogReturn => {
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: undefined,
    showCancel: false,
    confirmText: 'OK',
    cancelText: 'Cancel',
  });

  const showDialog = useCallback((config: Omit<DialogState, 'isOpen'>) => {
    setDialogState({
      ...config,
      isOpen: true,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState(prev => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const showSuccess = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showDialog({
      title,
      message,
      type: 'success',
      onConfirm,
      showCancel: false,
      confirmText: 'OK',
    });
  }, [showDialog]);

  const showError = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showDialog({
      title,
      message,
      type: 'error',
      onConfirm,
      showCancel: false,
      confirmText: 'OK',
    });
  }, [showDialog]);

  const showWarning = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showDialog({
      title,
      message,
      type: 'warning',
      onConfirm,
      showCancel: false,
      confirmText: 'OK',
    });
  }, [showDialog]);

  const showInfo = useCallback((title: string, message: string, onConfirm?: () => void) => {
    showDialog({
      title,
      message,
      type: 'info',
      onConfirm,
      showCancel: false,
      confirmText: 'OK',
    });
  }, [showDialog]);

  const showConfirm = useCallback((
    title: string, 
    message: string, 
    onConfirm: () => void
  ) => {
    showDialog({
      title,
      message,
      type: 'warning',
      onConfirm: () => {
        onConfirm();
        closeDialog();
      },
      showCancel: true,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
    });
  }, [showDialog, closeDialog]);

  return {
    dialogState,
    showDialog,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    closeDialog,
  };
};
